import React, { memo, useCallback } from "react";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { PartsRail, NumberTile, OutcomeWords, statusSentence } from "./QuestionProgress";
import { a } from "../appColors";

export const SIDEBAR_WIDTH = 300;
export const SIDEBAR_COLLAPSED_WIDTH = 62;

/** Strip the figure placeholders so a preview reads as a sentence. */
const previewOf = (text = "") => text.replace(/\[IMAGE_\d+\]/g, " ").replace(/\s+/g, " ").trim();

/**
 * One question in the list.
 *
 * Open, it is a card: the number, the opening words, a rail of one block per
 * part, and once marked, what the marker made of it. Collapsed, the words go
 * and the number and rail stay — the same two things, smaller, rather than a
 * different component that has to be kept in step.
 */
const QuestionRow = memo(({ group, isSelected, isCollapsed, onSelect }) => {
  const sentence = statusSentence(group);

  const card = (
    <Flex
      as="button"
      type="button"
      aria-label={`Question ${group.number}. ${sentence}`}
      aria-current={isSelected ? "true" : undefined}
      direction="column"
      gap="8px"
      width="100%"
      px={isCollapsed ? "6px" : "10px"}
      py="8px"
      align={isCollapsed ? "center" : "stretch"}
      borderRadius="10px"
      bg={isSelected ? a.surfaceSubtle : a.surface}
      border="1px solid"
      borderColor={isSelected ? a.ink : a.edge}
      // The selected card is lifted by its ink, not by a shadow, so the
      // column stays flat and quiet while you scan it.
      boxShadow={isSelected ? `inset 0 0 0 1px ${a.ink}` : "none"}
      color={a.text}
      textAlign="left"
      cursor="pointer"
      transition="border-color 0.14s, background 0.14s"
      _hover={{ borderColor: a.ink, bg: a.surfaceSubtle }}
      _focusVisible={{ outline: "2px solid", outlineColor: a.ink, outlineOffset: "2px" }}
      onClick={() => onSelect(group.number)}
    >
      <Flex align="center" gap="9px" width="100%" justify={isCollapsed ? "center" : "flex-start"}>
        <NumberTile number={group.number} progress={group.progress} />
        {!isCollapsed && (
          <Text fontSize="11px" lineHeight="15px" noOfLines={1} flex="1" minW={0}>
            {previewOf(group.preview) || "—"}
          </Text>
        )}
      </Flex>

      <PartsRail segments={group.partStates} compact={isCollapsed} />

      {!isCollapsed && (
        group.isMarked ? (
          <OutcomeWords tally={group.tally} />
        ) : (
          <Text fontSize="9px" lineHeight="14px" color={a.textSub}>
            {sentence}
          </Text>
        )
      )}
    </Flex>
  );

  return isCollapsed ? (
    <Tooltip label={`Question ${group.number} — ${sentence}`} placement="right" hasArrow fontSize="11px">
      <Box width="100%">{card}</Box>
    </Tooltip>
  ) : (
    card
  );
});
QuestionRow.displayName = "QuestionRow";

/**
 * The list of questions beside the paper (Figma 193:54, 222:155).
 *
 * Moving between questions from here is what keeps the paper from being one
 * long scroll, so this carries everything needed to choose: which question,
 * how far it has been taken, and once marked, how it went.
 */
const QuestionSidebar = ({
  groups,
  selectedNumber,
  onSelect,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const handleSelect = useCallback((n) => onSelect?.(n), [onSelect]);
  const Chevron = isCollapsed ? ChevronDoubleRightIcon : ChevronDoubleLeftIcon;

  const answeredAll = groups.filter((g) => g.progress === "complete").length;

  return (
    <Flex
      as="nav"
      aria-label="Questions"
      direction="column"
      gap="10px"
      width={`${isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px`}
      flexShrink={0}
      height="100%"
      minH={0}
      p="10px"
      bg={a.panel}
      border="1px solid"
      borderColor={a.panelBorder}
      borderRadius="14px"
      overflow="hidden"
      transition="width 0.2s ease"
    >
      <Flex align="center" justify={isCollapsed ? "center" : "space-between"} gap="8px" flexShrink={0}>
        {!isCollapsed && (
          <Text fontSize="10px" color={a.textSub} noOfLines={1}>
            {answeredAll} of {groups.length} done
          </Text>
        )}
        <Tooltip
          label={isCollapsed ? "Show the questions" : "Show numbers only"}
          placement="right"
          hasArrow
          fontSize="11px"
        >
          <Flex
            as="button"
            type="button"
            aria-label={isCollapsed ? "Expand the question list" : "Collapse the question list"}
            aria-expanded={!isCollapsed}
            align="center"
            justify="center"
            boxSize="26px"
            flexShrink={0}
            borderRadius="7px"
            color={a.text}
            cursor="pointer"
            _hover={{ bg: a.hover }}
            onClick={onToggleCollapse}
          >
            <Chevron style={{ width: 15, height: 15 }} />
          </Flex>
        </Tooltip>
      </Flex>

      {/* The list scrolls on its own; the bar is hidden so the column keeps
          its shape, and it stays reachable by wheel and keyboard. */}
      <Flex
        direction="column"
        gap="8px"
        overflowY="auto"
        flex="1"
        minH={0}
        sx={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {groups.map((group) => (
          <QuestionRow
            key={group.number}
            group={group}
            isSelected={group.number === selectedNumber}
            isCollapsed={isCollapsed}
            onSelect={handleSelect}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export default memo(QuestionSidebar);
